import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError, tap, finalize } from 'rxjs/operators';
import { LeadResumeService } from '@app/services/lead-resume/lead-resume.service';
import { LoaderService } from '@app/services/general/loader.service';
import { Store } from '@ngrx/store';
import { loadData, loadDataSuccess, setData } from 'src/storage/general.actions';
import { LeadResumeSyncService } from '@app/services/lead-resume/lead-resume-store-sync.service';
import { OrquestadorService } from '@app/services/orquestador/orquestador.service';
import { ensureNewFlowSessionContextOrRedirect } from '@app/utils/new-flow-session.util';



/**
 * Guard funcional de Angular 17 para procesar el parámetro idLead
 * Si existe el parámetro, obtiene los datos del lead antes de activar la ruta
 *
 * Uso:
 * ```typescript
 * {
 *   path: 'inicio',
 *   component: InicioComponent,
 *   canActivate: [leadResumeGuard]
 * }
 * ```
 */
export const leadResumeGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot
): Observable<boolean> | boolean => {
  const leadResumeService = inject(LeadResumeService);
  const loaderService = inject(LoaderService);
  const router = inject(Router);
  const store = inject(Store);
  const leadResumeSyncService = inject(LeadResumeSyncService);
  const orquestadorService = inject(OrquestadorService);

  // Obtener el parámetro idLead de la query string
  const idLeadParameter = route.queryParamMap.get('recordId');

  // Si no existe el parámetro, permitir el acceso normal
  if (!idLeadParameter) {
    console.log('ℹ️ No se encontró parámetro recordId, continuando navegación normal');
    const ok = ensureNewFlowSessionContextOrRedirect(store, router);
    if (!ok) return false;
    return true;
  }

  console.log('🔐 Parámetro recordId detectado, procesando...');

  // Si ya existe data cargada, no volver a cargar
  if (leadResumeService.hasLoadedData()) {
    console.log('✅ Datos del lead ya cargados, permitiendo acceso');
    return true;
  }

  // Mostrar loader mientras se resuelve el servicio
  loaderService.show();

  // Obtener los datos del lead
  return leadResumeService.getLeadResume(idLeadParameter).pipe(
    tap(response => {
      console.log('✅ Lead procesado exitosamente', response);
      // Despachar acción para cargar locations después de retoma exitosa
      console.log('📍 Cargando store de locations...');
      const body = {
        getInitialData: true
      };
      
      // leadResumeSyncService.syncAllLeadResumeData();
      // store.dispatch(setData({ key: 'isLeadResume', value: true, merge: true }));
      saveLocationsToStore(body, store, orquestadorService, leadResumeSyncService);
      // store.dispatch(loadData({ body }));
    }),
    map(response => {
      console.log('✅ Permitiendo acceso');
      return true;
    }),
    catchError(error => {
      console.error('❌ Error al procesar lead:', error);
      console.log('🔀 Redirigiendo a problemas técnicos...');
      // En caso de error, redirigir a la página de problemas técnicos
      router.navigate(['seguros/hogar/arriendo/cotizador/problemas-tecnicos']);
      return of(false);
    }),
    finalize(() => {
      // Ocultar loader cuando termine el proceso (éxito o error)
      loaderService.hide();
    })
  );
};

/**
 * Guard opcional que valida si existe información del lead cargada
 * Útil para proteger rutas que requieren que exista un lead activo
 *
 * Uso:
 * ```typescript
 * {
 *   path: 'resumen',
 *   component: ResumenComponent,
 *   canActivate: [requireLeadDataGuard]
 * }
 * ```
 */
export const requireLeadDataGuard: CanActivateFn = (): boolean => {
  const leadResumeService = inject(LeadResumeService);
  const router = inject(Router);

  const hasData = leadResumeService.hasLoadedData();

  if (!hasData) {
    console.warn('⚠️ No hay datos de lead cargados, redirigiendo a inicio');
    router.navigate(['/inicio']);
    return false;
  }

  console.log('✅ Datos de lead verificados, permitiendo acceso');
  return true;
};


const saveLocationsToStore = (body: any, store: Store, orquestadorService: OrquestadorService, leadResumeSyncService: LeadResumeSyncService) => {

  orquestadorService.postOrquestadorIntermedia(body).pipe(
    map((response: any) => {


      const { initialData, locations } = response.data;
      console.log('Data Inicial recibida para initialData:', initialData);
      
      
      console.log('Data Inicial recibida para locations:', locations);
      // store.dispatch(removeData({ key: 'locations' })); // Limpiar antes de sincronizar
      if (locations) {
        // store.dispatch(setData({ key: 'locations', value:{...locations}, merge: true }));
        
        store.dispatch(loadDataSuccess({ data: { initialData, locations } }));
        leadResumeSyncService.syncAllLeadResumeData();
        store.dispatch(setData({ key: 'isLeadResume', value: true, merge: true }));
      }
      


    }),
    catchError((error): any => {
      
      return console.error('Effect: Error occurred', error);
      
    })
  ).subscribe();
}